import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IssuerLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/issuer/login`, formData);
      localStorage.setItem('issuer_token', response.data.token);
      localStorage.setItem('issuer_data', JSON.stringify(response.data.issuer));
      toast.success("Login successful!");
      navigate("/issuer/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Settings className="h-10 w-10 text-slate-900" />
            <span className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <h1 className="text-3xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Welcome Back</h1>
          <p className="text-slate-600">Sign in to manage your certificates</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="login-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="you@example.com"
                required
                className="h-11 rounded-lg border-slate-200"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                required
                className="h-11 rounded-lg border-slate-200"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg py-3 font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Don't have an account?{" "}
              <button onClick={() => navigate("/issuer/signup")} className="text-blue-600 hover:underline" data-testid="signup-link">
                Sign up
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-slate-700" data-testid="back-home-link">
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssuerLogin;
